"use client";

import Image from "next/image";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { MoveHorizontal } from "lucide-react";

import { cn } from "@/lib/utils";

interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeLabel?: string;
  afterLabel?: string;
  ariaLabel: string;
  alt: string;
  autoplay?: boolean;
  className?: string;
  imageClassName?: string;
  priority?: boolean;
  sizes?: string;
}

export function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeLabel = "Before",
  afterLabel = "New look",
  ariaLabel,
  alt,
  autoplay = false,
  className,
  imageClassName,
  priority = false,
  sizes = "(max-width: 1024px) 100vw, 50vw",
}: BeforeAfterSliderProps) {
  const [afterRevealPercent, setAfterRevealPercent] = useState(autoplay ? 0 : 50);
  const [isAnimating, setIsAnimating] = useState(false);
  const [afterImageFailed, setAfterImageFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timersRef = useRef<number[]>([]);

  const cancelAutoplay = useCallback(() => {
    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    timersRef.current = [];
    setIsAnimating(false);
  }, []);

  useEffect(() => {
    if (!autoplay) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      const reducedMotionTimer = window.setTimeout(() => setAfterRevealPercent(50), 0);
      timersRef.current = [reducedMotionTimer];
      return cancelAutoplay;
    }

    const revealTimer = window.setTimeout(() => {
      setIsAnimating(true);
      setAfterRevealPercent(100);
    }, 1200);
    const returnTimer = window.setTimeout(() => {
      setAfterRevealPercent(0);
    }, 2300);
    const settleTimer = window.setTimeout(() => {
      setAfterRevealPercent(50);
    }, 3400);
    const finishTimer = window.setTimeout(() => {
      setIsAnimating(false);
    }, 4400);

    timersRef.current = [revealTimer, returnTimer, settleTimer, finishTimer];
    return cancelAutoplay;
  }, [autoplay, cancelAutoplay]);

  const updateFromClientX = useCallback((clientX: number) => {
    const container = containerRef.current;
    if (!container) return;

    const bounds = container.getBoundingClientRect();
    const position = Math.max(0, Math.min(clientX - bounds.left, bounds.width));
    setAfterRevealPercent((position / bounds.width) * 100);
  }, []);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    cancelAutoplay();
    event.currentTarget.setPointerCapture(event.pointerId);
    updateFromClientX(event.clientX);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    updateFromClientX(event.clientX);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 10 : 5;
    let nextValue: number | null = null;

    if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      nextValue = Math.max(0, afterRevealPercent - step);
    } else if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      nextValue = Math.min(100, afterRevealPercent + step);
    } else if (event.key === "Home") {
      nextValue = 0;
    } else if (event.key === "End") {
      nextValue = 100;
    }

    if (nextValue === null) return;
    event.preventDefault();
    cancelAutoplay();
    setAfterRevealPercent(nextValue);
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "group relative w-full cursor-col-resize touch-pan-y select-none overflow-hidden bg-black",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black",
        className
      )}
      role="slider"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(afterRevealPercent)}
      aria-valuetext={`${Math.round(afterRevealPercent)}% ${afterLabel.toLowerCase()} visible`}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
    >
      <Image
        src={beforeImage}
        alt={alt}
        fill
        className={cn("object-cover", imageClassName)}
        draggable={false}
        priority={priority}
        sizes={sizes}
      />

      {!afterImageFailed ? (
        <div
          className={cn(
            "absolute inset-0 overflow-hidden",
            isAnimating && "transition-[clip-path] duration-[900ms] ease-in-out"
          )}
          style={{ clipPath: `inset(0 ${100 - afterRevealPercent}% 0 0)` }}
        >
          <Image
            src={afterImage}
            alt=""
            fill
            className={cn("object-cover", imageClassName)}
            draggable={false}
            priority={priority}
            sizes={sizes}
            onError={() => setAfterImageFailed(true)}
          />
        </div>
      ) : null}

      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-between p-3 sm:p-4">
        <span className="rounded-full bg-black/65 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm sm:text-sm">
          {beforeLabel}
        </span>
        <span className="rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-black backdrop-blur-sm sm:text-sm">
          {afterLabel}
        </span>
      </div>

      {!afterImageFailed ? (
        <div
          className={cn(
            "pointer-events-none absolute inset-y-0 z-10 w-px bg-white/80",
            isAnimating && "transition-[left] duration-[900ms] ease-in-out"
          )}
          style={{ left: `${afterRevealPercent}%` }}
        >
          <div className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black shadow-xl transition-transform group-hover:scale-105">
            <MoveHorizontal className="h-5 w-5" aria-hidden />
          </div>
        </div>
      ) : null}
    </div>
  );
}
